import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ToastController } from '@ionic/angular';
import { UserService } from '../auth/user.service';
import { ByDayCollection } from '../../models/collections.model';
import { AgendaJourActiviteInterne } from '../../models/agenda/agenda-jour.model';
import { environment } from '../../../../environments/environment';
import { ActiviteInterne } from '../../models/agenda/activite-interne.model';
import { TypeActivite } from '../../models/agenda/activite.model';
import { CrActiviteInterne } from '../../models/activite-interne/activite-interne.model';
import { NetworkProvider } from '../network/network';

@Injectable({ providedIn: 'root' })
export class AgendaActiviteInterneService {
  constructor(
    private readonly userService: UserService,
    private readonly http: HttpClient,
    private readonly toastCtrl: ToastController,
    private readonly translate: TranslateService,
    private readonly networkProvider: NetworkProvider
  ) {}

  /**
   * Retourne les jours contenant les données de activité interne autour de la date
   * @param displayedDate Date courante
   */
  getAgendaActiviteInterne(displayedDate: Date): Observable<ByDayCollection<AgendaJourActiviteInterne>> {
    const dateDebut = moment(displayedDate).subtract(7, 'day').toISOString();
    const dateFin = moment(displayedDate).add(7, 'day').toISOString();
    const idIntervenant = this.userService.getId();
    let url = environment.apis.API_DACTECH_BACK_ROOT + '/v1/crActiviteInternes';
    return this.http
      .get<CrActiviteInterne[]>(url, {
        params: {
          id_intervenant: idIntervenant,
          saisi_apres_date: dateDebut,
          saisi_avant_date: dateFin,
        },
      })
      .pipe(
        map((result) => {
          return this.mapperResponse(result);
        }),
        catchError(() => {
          if (this.networkProvider.isConnected()) {
            this.presentToast(this.translate.instant('AGENDA.AI.TOAST.ERROR')).then();
          }
          return of(new ByDayCollection<AgendaJourActiviteInterne>());
        })
      );
  }

  public mapperResponse(data: CrActiviteInterne[]): ByDayCollection<AgendaJourActiviteInterne> {
    let response: ByDayCollection<AgendaJourActiviteInterne> = new ByDayCollection<AgendaJourActiviteInterne>();
    for (let aiResponse of data) {
      let activiteInterne = this.mapActiviteInterne(aiResponse);
      let date = moment(activiteInterne.dateDebut);

      let agenda: AgendaJourActiviteInterne = { activitesInternes: [] };
      if (response.has(date)) {
        agenda = response.get(date);
      }

      agenda.activitesInternes.push(activiteInterne);
      response.put(date, agenda);
    }
    return response;
  }

  public mapActiviteInterne(aiResponse: CrActiviteInterne): ActiviteInterne {
    return {
      id: aiResponse.id_activite_interne,
      idCr: aiResponse.id,
      activiteDuree: aiResponse.cr_activite_interne_duree,
      deplacementDuree: aiResponse.cr_deplacement_duree,
      commentaireIntervenant: aiResponse.cr_commentaire_intervenant,
      dateDebut: new Date(aiResponse.cr_activite_interne_date_debut),
      dateFin: new Date(aiResponse.cr_activite_interne_date_fin),
      libelle: aiResponse.cr_type_activite_realisee,
      type: TypeActivite.Interne,
      numeroOt: aiResponse.numero_ot,
      dkCodeSite: aiResponse.dk_code_site,
      libelleSite: aiResponse.libelle_site,
    };
  }

  private async presentToast(message: string) {
    let toast = await this.toastCtrl.create({
      message: message,
      duration: 5000,
    });
    toast.present().then();
  }
}
